/**
 * Elite Batch 24 Part 4 — HR & People Ops (Workflows 31-40)
 *
 * PRODUCTION-GRADE ENHANCEMENTS:
 * 1. Case stamping — every workflow starts with caseStampNode (node 2)
 * 2. Confidence-gated AI — aiNodeV2 outputs confidence_score, evidence, needs_human_review
 * 3. Confidence routing — confidenceGate routes high-confidence auto vs low-confidence human
 * 4. Post-execution verification — verifyNode confirms actions actually worked
 * 5. Exception handling / logging — sheetsNode or exceptionNode for audit trail
 *
 * CORRECT builder field names:
 * - conditionData (NOT conditionDetails)
 * - approvalGateData (NOT approvalDetails)
 * - delayAction (NOT delayDetails)
 * - code (NOT runCodeDetails.code)
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED HELPERS — BATCH 24 ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function delayNode(
  id: string,
  label: string,
  y: number,
  time: number,
  timeFormat: string,
  x = 80,
): any {
  return n(
    id,
    "delayAction",
    label,
    y,
    {
      delayAction: { time, timeFormat },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function exceptionNode(id: string, label: string, y: number, x = 80): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Exception handler: categorize, log, and route
const errorType = variables.error_message || variables.verify_status || 'unknown';
const isTransient = ['timeout', '429', '503', '502'].some(e => String(errorType).includes(e));
const retryCount = parseInt(variables.retry_count || '0');
output.exception_type = isTransient ? 'transient' : 'permanent';
output.should_retry = isTransient && retryCount < 3 ? 'yes' : 'no';
output.retry_count = String(retryCount + 1);
output.exception_summary = 'Error: ' + errorType + ' | Type: ' + output.exception_type + ' | Retry: ' + output.should_retry;
output.escalate_to_human = (!isTransient || retryCount >= 3) ? 'yes' : 'no';
output.logged_at = new Date().toISOString();`,
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 31-40: HR & People Ops ============

export const ELITE_BATCH24_PART4 = [
  // ─── 31. Elite: Resume Screening + Fit Scoring ───
  {
    name: "Elite: Resume Screening + Fit Scoring",
    description:
      "Processes new job applications by extracting resume data, scoring candidate fit against job criteria from Greenhouse, and automatically advancing high-confidence matches. Borderline candidates are routed to the recruiter for manual review.",
    category: "HR & People Ops",
    tags: [
      "recruiting",
      "resume-screening",
      "greenhouse",
      "ai-scoring",
      "hiring",
    ],
    nodes: [
      trigger("Greenhouse: New application received", "webhook_app", {
        source: "greenhouse",
        event: "application.created",
        howItStarts:
          "Fires when a candidate submits an application in Greenhouse",
        setup:
          "Connect Greenhouse webhook for application.created events. Payload includes application_id, candidate_id, job_id, resume_url, candidate_name, and candidate_email.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "resume_screening_fit_scoring",
      ),
      aiNodeV2(
        "3",
        "AI: Score resume fit with evidence",
        280,
        "You are an expert technical recruiter. Evaluate the candidate resume against the job requirements. Score fit on: skills_match (0-100), experience_relevance (0-100), education_fit (0-100), and overall_fit (weighted average: skills 50%, experience 35%, education 15%). Provide specific evidence for each score citing resume details. Flag any red_flags (gaps, job hopping, mismatched seniority) and green_flags (exact tech stack match, industry experience, referral).",
        "Candidate: {variables.candidate_name} — Resume: {variables.resume_text} — Job: {variables.job_title} — Requirements: {variables.job_requirements} — Preferred: {variables.preferred_qualifications} — Level: {variables.seniority_level}",
        [
          { field: "overall_fit", as: "fit_score", type: "number" },
          { field: "skills_match", as: "skills_score", type: "number" },
          {
            field: "experience_relevance",
            as: "experience_score",
            type: "number",
          },
          { field: "recommendation", as: "ai_recommendation", type: "string" },
        ],
      ),
      n("4", "hitApi", "Greenhouse: Fetch job criteria + requirements", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://harvest.greenhouse.io/v1/jobs/{variables.job_id}",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_api_key}" },
          ],
          body: [],
          variables: [
            { name: "title", customName: "job_title", type: "string" },
            {
              name: "departments[0].name",
              customName: "department",
              type: "string",
            },
            {
              name: "custom_fields.requirements",
              customName: "job_requirements",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: fit score accuracy", 520, "75"),
      n("6", "hitApi", "Greenhouse: Advance candidate stage", 640, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/applications/{variables.application_id}/advance",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "from_stage_id", value: "{variables.current_stage_id}" },
          ],
          variables: [
            {
              name: "id",
              customName: "advanced_application_id",
              type: "string",
            },
            {
              name: "status",
              customName: "application_status",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Recruiter: Review borderline candidate",
        640,
        "AI scored {variables.candidate_name} at {variables.fit_score}/100 fit for {variables.job_title} with {variables.ai_confidence}% confidence. Skills: {variables.skills_score}, Experience: {variables.experience_score}. Recommendation: {variables.ai_recommendation}. Approve to advance.",
        24,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Stage updated in Greenhouse",
        760,
        "GET",
        "https://harvest.greenhouse.io/v1/applications/{variables.application_id}",
        [{ key: "Authorization", value: "Basic {greenhouse_api_key}" }],
        [
          {
            name: "current_stage.name",
            customName: "current_stage_name",
            type: "string",
          },
          { name: "status", customName: "verified_app_status", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Stage advanced successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Resume screening audit trail",
        1000,
        "ResumeScreening",
        '[["{variables.case_id}","{variables.candidate_name}","{variables.job_title}","{variables.fit_score}","{variables.ai_recommendation}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 32. Elite: Interview Feedback Compiler ───
  {
    name: "Elite: Interview Feedback Compiler",
    description:
      "Triggers after an interview is completed in Greenhouse, compiles all interviewer scorecards, uses AI to synthesize a balanced summary with consensus and dissent points, and emails it to the hiring manager. Ambiguous cases go to the recruiting lead.",
    category: "HR & People Ops",
    tags: [
      "interviewing",
      "feedback",
      "greenhouse",
      "hiring-manager",
      "synthesis",
    ],
    nodes: [
      trigger("Greenhouse: Interview completed", "webhook_app", {
        source: "greenhouse",
        event: "scorecard.created",
        howItStarts:
          "Fires when the final interviewer submits their scorecard for a candidate",
        setup:
          "Connect Greenhouse webhook for scorecard.created events. Configure to fire when all interviewers for a stage have submitted. Payload includes application_id, candidate_id, interview_id, and scorecard data.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "interview_feedback_compiler",
      ),
      aiNodeV2(
        "3",
        "AI: Compile + synthesize interview feedback",
        280,
        "You are a recruiting operations expert. Synthesize multiple interviewer scorecards into a hiring manager summary. Include: overall_recommendation (strong_hire, hire, lean_hire, lean_no, no_hire, strong_no), consensus_points (what all interviewers agreed on), dissent_points (where opinions differed), key_strengths (top 3), key_concerns (top 3), and a concise executive_summary (3-4 sentences). Be balanced and cite specific interviewer feedback.",
        "Candidate: {variables.candidate_name} — Position: {variables.job_title} — Scorecards: {variables.scorecards_json} — Interview stage: {variables.interview_stage} — Total interviewers: {variables.interviewer_count}",
        [
          {
            field: "overall_recommendation",
            as: "recommendation",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_strengths", as: "key_strengths", type: "string" },
          { field: "key_concerns", as: "key_concerns", type: "string" },
        ],
      ),
      n("4", "hitApi", "Greenhouse: Fetch all scorecards for candidate", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://harvest.greenhouse.io/v1/applications/{variables.application_id}/scorecards",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_api_key}" },
          ],
          body: [],
          variables: [
            { name: "length", customName: "interviewer_count", type: "number" },
            { name: "", customName: "scorecards_json", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: synthesis accuracy", 520, "75"),
      emailNode(
        "6",
        "Email: Send feedback summary to hiring manager",
        640,
        "{variables.hiring_manager_email}",
        "Interview Feedback Summary: {variables.candidate_name} — {variables.job_title}",
        "Hi {variables.hiring_manager_name},\\n\\nHere is the compiled interview feedback for {variables.candidate_name} ({variables.job_title}):\\n\\n**Recommendation:** {variables.recommendation}\\n\\n**Summary:**\\n{variables.executive_summary}\\n\\n**Key Strengths:**\\n{variables.key_strengths}\\n\\n**Key Concerns:**\\n{variables.key_concerns}\\n\\nInterviewers: {variables.interviewer_count} | Case: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "Recruiting lead: Review ambiguous synthesis",
        640,
        "AI synthesized feedback for {variables.candidate_name} ({variables.job_title}) with {variables.ai_confidence}% confidence. Recommendation: {variables.recommendation}. Review needed — interviewers may have conflicting signals.",
        12,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Summary email delivered",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={variables.hiring_manager_email}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          {
            name: "TotalCount",
            customName: "summary_emails_sent",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "email_delivery_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Summary delivered successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Interview feedback audit trail",
        1000,
        "InterviewFeedback",
        '[["{variables.case_id}","{variables.candidate_name}","{variables.job_title}","{variables.recommendation}","{variables.interviewer_count}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 33. Elite: Offer Approval Chain ───
  {
    name: "Elite: Offer Approval Chain",
    description:
      "Triggers when a candidate reaches the offer stage in Greenhouse, validates the proposed compensation against band data from BambooHR, and routes compliant offers for VP auto-approval. Out-of-band offers require HR and finance sign-off.",
    category: "HR & People Ops",
    tags: [
      "offers",
      "compensation",
      "greenhouse",
      "bamboohr",
      "approval-chain",
    ],
    nodes: [
      trigger("Greenhouse: Candidate reaches offer stage", "webhook_app", {
        source: "greenhouse",
        event: "application.offer_created",
        howItStarts:
          "Fires when a hiring manager creates an offer for a candidate in Greenhouse",
        setup:
          "Connect Greenhouse webhook for offer creation events. Payload includes application_id, candidate_name, job_id, offer_amount, currency, and start_date.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "offer_approval_chain",
      ),
      aiNodeV2(
        "3",
        "AI: Validate comp against band",
        280,
        "You are a compensation analyst. Compare the proposed offer against the role's compensation band. Check: (1) base_salary within min-max range, (2) equity within guidelines, (3) signing bonus if applicable within policy. Flag if the offer is above_band, within_band, or below_band. Calculate percentile_in_band. Recommend adjustments if out of band. Factor in location, experience level, and market data.",
        "Candidate: {variables.candidate_name} — Role: {variables.job_title} — Level: {variables.level} — Proposed base: ${variables.offer_base} — Proposed equity: {variables.offer_equity} — Location: {variables.location} — Band min: ${variables.band_min} — Band mid: ${variables.band_mid} — Band max: ${variables.band_max}",
        [
          { field: "band_status", as: "band_status", type: "string" },
          {
            field: "percentile_in_band",
            as: "band_percentile",
            type: "number",
          },
          {
            field: "recommendation",
            as: "comp_recommendation",
            type: "string",
          },
          {
            field: "adjustment_needed",
            as: "adjustment_needed",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "BambooHR + Greenhouse: Fetch comp band data", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/meta/tables/compensation",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "fields[0].min", customName: "band_min", type: "string" },
            { name: "fields[0].mid", customName: "band_mid", type: "string" },
            { name: "fields[0].max", customName: "band_max", type: "string" },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: comp validation accuracy",
        520,
        "75",
      ),
      n("6", "hitApi", "Auto-route: VP approval for in-band offers", 640, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/applications/{variables.application_id}/offers/{variables.offer_id}/approve",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "approver_id", value: "{variables.vp_approver_id}" },
            {
              key: "note",
              value:
                "Auto-approved: {variables.band_status} at {variables.band_percentile}th percentile. AI confidence: {variables.ai_confidence}%.",
            },
          ],
          variables: [
            { name: "id", customName: "approval_id", type: "string" },
            { name: "status", customName: "approval_status", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "HR + Finance: Review out-of-band offer",
        640,
        "Offer for {variables.candidate_name} ({variables.job_title}) is {variables.band_status} at {variables.band_percentile}th percentile. Base: ${variables.offer_base}, Band: ${variables.band_min}-${variables.band_max}. AI recommendation: {variables.comp_recommendation}. Confidence: {variables.ai_confidence}%.",
        48,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Approval recorded in Greenhouse",
        760,
        "GET",
        "https://harvest.greenhouse.io/v1/applications/{variables.application_id}/offers",
        [{ key: "Authorization", value: "Basic {greenhouse_api_key}" }],
        [
          { name: "[0].status", customName: "offer_status", type: "string" },
          { name: "[0].id", customName: "verified_offer_id", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Approval recorded successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Offer approval audit trail",
        1000,
        "OfferApprovals",
        '[["{variables.case_id}","{variables.candidate_name}","{variables.job_title}","{variables.offer_base}","{variables.band_status}","{variables.band_percentile}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 34. Elite: New Hire Onboarding Orchestrator ───
  {
    name: "Elite: New Hire Onboarding Orchestrator",
    description:
      "Triggers when a hire is confirmed in BambooHR to generate a personalized onboarding plan using AI based on role, department, and location. High-confidence plans auto-create Asana task lists; edge cases go to the HR manager for customization.",
    category: "HR & People Ops",
    tags: ["onboarding", "new-hire", "bamboohr", "asana", "task-automation"],
    nodes: [
      trigger("BambooHR: Hire confirmed", "webhook_app", {
        source: "bamboohr",
        event: "employee.created",
        howItStarts:
          "Fires when a new employee record is created in BambooHR after offer acceptance",
        setup:
          "Connect BambooHR webhook for employee.created events. Payload includes employee_id, name, email, department, job_title, start_date, location, and manager_id.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "new_hire_onboarding",
      ),
      aiNodeV2(
        "3",
        "AI: Generate personalized onboarding plan",
        280,
        "You are an HR onboarding specialist. Create a comprehensive 90-day onboarding plan personalized to the role, department, and location. Include: week_1_tasks (admin, setup, introductions), week_2_4_tasks (training, shadowing, first projects), month_2_3_tasks (deeper projects, cross-team intros, first review). Each task should have title, description, assignee_type (new_hire, manager, IT, HR), and due_day (relative to start). Output as structured JSON with total_tasks count.",
        "New hire: {variables.employee_name} — Role: {variables.job_title} — Department: {variables.department} — Location: {variables.location} — Start date: {variables.start_date} — Manager: {variables.manager_name} — Team size: {variables.team_size}",
        [
          {
            field: "total_tasks",
            as: "total_onboarding_tasks",
            type: "number",
          },
          {
            field: "onboarding_plan",
            as: "onboarding_plan_json",
            type: "string",
          },
          {
            field: "first_week_priority",
            as: "first_week_priority",
            type: "string",
          },
          {
            field: "special_considerations",
            as: "special_notes",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch employee details", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{variables.employee_id}?fields=firstName,lastName,department,jobTitle,hireDate,location,supervisorEId",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "firstName", customName: "first_name", type: "string" },
            { name: "department", customName: "department", type: "string" },
            { name: "jobTitle", customName: "job_title", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: plan quality", 520, "75"),
      n("6", "hitApi", "Asana: Create onboarding task project", 640, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Onboarding: {variables.employee_name} — {variables.job_title}","projects":["{asana_onboarding_project_id}"],"notes":"AI-generated 90-day onboarding plan.\\n\\nTotal tasks: {variables.total_onboarding_tasks}\\nFirst week priority: {variables.first_week_priority}\\nSpecial notes: {variables.special_notes}\\n\\nFull plan:\\n{variables.onboarding_plan_json}","due_on":"{variables.plan_due_date}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_project_id",
              type: "string",
            },
            { name: "data.name", customName: "project_name", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "HR manager: Customize onboarding plan",
        640,
        "AI generated a {variables.total_onboarding_tasks}-task onboarding plan for {variables.employee_name} ({variables.job_title}) with {variables.ai_confidence}% confidence. Special notes: {variables.special_notes}. Review and approve to create tasks.",
        24,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Asana tasks created",
        760,
        "GET",
        "https://app.asana.com/api/1.0/tasks/{variables.asana_project_id}",
        [{ key: "Authorization", value: "Bearer {asana_token}" }],
        [
          {
            name: "data.gid",
            customName: "verified_project_id",
            type: "string",
          },
          {
            name: "data.completed",
            customName: "project_completed",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Tasks created successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Onboarding audit trail",
        1000,
        "Onboarding",
        '[["{variables.case_id}","{variables.employee_name}","{variables.job_title}","{variables.department}","{variables.total_onboarding_tasks}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 35. Elite: Training Compliance Tracker ───
  {
    name: "Elite: Training Compliance Tracker",
    description:
      "Runs on a weekly schedule to identify employees with overdue or at-risk training completions from KnowBe4 data. AI prioritizes reminders by risk level and regulatory deadline, auto-sending reminders for clear cases and routing ambiguous situations to HR compliance.",
    category: "HR & People Ops",
    tags: ["training", "compliance", "knowbe4", "reminders", "scheduling"],
    nodes: [
      trigger("Schedule: Weekly training compliance check", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to identify overdue and at-risk training assignments",
        setup:
          "Configure KnowBe4 API access for training completion data. Ensure email service is configured for reminder dispatch. Set compliance deadlines by training type.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "training_compliance_tracker",
      ),
      aiNodeV2(
        "3",
        "AI: Identify overdue + at-risk employees",
        280,
        "You are an HR compliance specialist. Analyze training completion data to identify: (1) overdue employees (past deadline), (2) at-risk employees (deadline within 7 days, not started or <50% complete), (3) upcoming (deadline within 30 days). For each, provide employee_name, training_name, deadline, completion_percent, risk_level (critical, high, medium, low), and recommended_action (urgent_reminder, standard_reminder, manager_escalation). Summarize totals and highest-risk items.",
        "Training data: {variables.training_data_json} — Current date: {variables.current_date} — Compliance requirements: SOC2 annual, HIPAA annual, Security Awareness quarterly, Anti-harassment annual",
        [
          { field: "overdue_count", as: "overdue_count", type: "number" },
          { field: "at_risk_count", as: "at_risk_count", type: "number" },
          { field: "reminder_list", as: "reminder_list_json", type: "string" },
          {
            field: "compliance_summary",
            as: "compliance_summary",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "KnowBe4: Fetch training completion data", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.knowbe4.com/v1/training/enrollments?status=not_started,in_progress&per_page=100",
          headers: [{ key: "Authorization", value: "Bearer {knowbe4_token}" }],
          body: [],
          variables: [
            {
              name: "enrollments",
              customName: "training_data_json",
              type: "string",
            },
            {
              name: "total_count",
              customName: "total_enrollments",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: risk assessment accuracy",
        520,
        "75",
      ),
      emailNode(
        "6",
        "Email: Send training reminders",
        640,
        "{variables.reminder_recipients}",
        "Action Required: Training compliance reminder",
        "Hi,\\n\\nThis is a reminder that you have outstanding training assignments that require completion:\\n\\n{variables.reminder_list_json}\\n\\nOverdue items: {variables.overdue_count}\\nAt-risk items: {variables.at_risk_count}\\n\\nPlease complete your assignments by the deadline to maintain compliance.\\n\\nCompliance tracking ID: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "HR Compliance: Review ambiguous cases",
        640,
        "AI identified {variables.overdue_count} overdue and {variables.at_risk_count} at-risk training assignments with {variables.ai_confidence}% confidence. Summary: {variables.compliance_summary}. Approve to send reminders.",
        24,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Reminders sent successfully",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=training-compliance&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          {
            name: "TotalCount",
            customName: "reminders_sent_count",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "reminder_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Reminders delivered",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Training compliance audit trail",
        1000,
        "TrainingCompliance",
        '[["{variables.case_id}","{variables.overdue_count}","{variables.at_risk_count}","{variables.compliance_summary}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 36. Elite: PTO Balance Validator ───
  {
    name: "Elite: PTO Balance Validator",
    description:
      "Processes PTO requests by checking the employee's balance and team coverage in BambooHR. AI validates eligibility and coverage impact. Clear approvals auto-process with calendar updates; requests needing judgment go to the manager.",
    category: "HR & People Ops",
    tags: ["pto", "time-off", "bamboohr", "auto-approval", "coverage"],
    nodes: [
      trigger("BambooHR: PTO request submitted", "webhook_app", {
        source: "bamboohr",
        event: "timeoff.request_created",
        howItStarts: "Fires when an employee submits a PTO request in BambooHR",
        setup:
          "Connect BambooHR webhook for time-off request events. Payload includes request_id, employee_id, employee_name, start_date, end_date, days_requested, and pto_type.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "pto_balance_validator",
      ),
      aiNodeV2(
        "3",
        "AI: Check balance + team coverage",
        280,
        "You are an HR operations specialist. Validate the PTO request: (1) sufficient balance remaining after request, (2) no blackout dates conflict, (3) team coverage is adequate (less than 30% of team out simultaneously), (4) no conflict with critical project deadlines. Output approval_recommendation (auto_approve, manager_review, deny), remaining_balance_after, coverage_impact (low, medium, high), and any conflicts_found.",
        "Employee: {variables.employee_name} — Request: {variables.start_date} to {variables.end_date} ({variables.days_requested} days) — PTO type: {variables.pto_type} — Current balance: {variables.current_pto_balance} days — Team members out same period: {variables.team_out_count}/{variables.team_size} — Blackout dates: {variables.blackout_dates}",
        [
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
          {
            field: "remaining_balance_after",
            as: "remaining_balance",
            type: "number",
          },
          { field: "coverage_impact", as: "coverage_impact", type: "string" },
          { field: "conflicts_found", as: "conflicts", type: "string" },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch PTO balance + team schedule", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{variables.employee_id}/time_off/calculator?end={variables.end_date}",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "balance",
              customName: "current_pto_balance",
              type: "number",
            },
            { name: "daysUsed", customName: "days_used_ytd", type: "number" },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: validation accuracy", 520, "75"),
      n("6", "hitApi", "BambooHR: Auto-approve + update calendar", 640, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/time_off/requests/{variables.request_id}/status",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "approved" },
            {
              key: "note",
              value:
                "Auto-approved: {variables.remaining_balance} days remaining, coverage impact: {variables.coverage_impact}. Case: {variables.case_id}",
            },
          ],
          variables: [
            { name: "status", customName: "request_status", type: "string" },
            { name: "id", customName: "approved_request_id", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "Manager: Review PTO request",
        640,
        "{variables.employee_name} requested {variables.days_requested} days PTO ({variables.start_date} to {variables.end_date}). Balance after: {variables.remaining_balance} days. Coverage impact: {variables.coverage_impact}. Conflicts: {variables.conflicts}. Confidence: {variables.ai_confidence}%.",
        24,
        420,
      ),
      verifyNode(
        "8",
        "Verify: PTO status updated",
        760,
        "GET",
        "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/time_off/requests/{variables.request_id}",
        [
          { key: "Authorization", value: "Basic {bamboohr_api_key}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          { name: "status", customName: "verified_pto_status", type: "string" },
          { name: "id", customName: "verified_request_id", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: PTO processed successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: PTO validation audit trail",
        1000,
        "PTOValidation",
        '[["{variables.case_id}","{variables.employee_name}","{variables.days_requested}","{variables.remaining_balance}","{variables.coverage_impact}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 37. Elite: Exit Interview Analyzer ───
  {
    name: "Elite: Exit Interview Analyzer",
    description:
      "Triggers on resignation events to analyze exit interview responses for themes and patterns using AI. Cross-references with BambooHR tenure and performance data. High-confidence insights post directly to Slack; ambiguous patterns go to the HRBP for review.",
    category: "HR & People Ops",
    tags: [
      "exit-interview",
      "attrition",
      "bamboohr",
      "sentiment-analysis",
      "retention",
    ],
    nodes: [
      trigger("BambooHR: Resignation submitted", "webhook_app", {
        source: "bamboohr",
        event: "employee.terminated",
        howItStarts:
          "Fires when an employee resignation is recorded and exit interview data is submitted",
        setup:
          "Connect BambooHR webhook for employee termination events. Ensure exit interview survey responses are accessible via API. Payload includes employee_id, termination_date, and termination_reason.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "exit_interview_analyzer",
      ),
      aiNodeV2(
        "3",
        "AI: Analyze exit themes + patterns",
        280,
        "You are an HR analytics expert specializing in employee attrition. Analyze the exit interview responses to identify: primary_departure_reason (compensation, growth, management, culture, work_life_balance, relocation, other), secondary_factors, sentiment_tone (bitter, neutral, positive, mixed), actionable_insights (specific changes that could have prevented departure), department_pattern (is this part of a trend in the department), and retention_risk_for_team (how likely are peers to follow).",
        "Employee: {variables.employee_name} — Department: {variables.department} — Tenure: {variables.tenure_months} months — Role: {variables.job_title} — Manager: {variables.manager_name} — Exit interview responses: {variables.exit_responses_json} — Department attrition rate (12mo): {variables.dept_attrition_rate}%",
        [
          {
            field: "primary_departure_reason",
            as: "departure_reason",
            type: "string",
          },
          {
            field: "actionable_insights",
            as: "actionable_insights",
            type: "string",
          },
          {
            field: "retention_risk_for_team",
            as: "team_retention_risk",
            type: "string",
          },
          { field: "sentiment_tone", as: "exit_sentiment", type: "string" },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch employee history", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{variables.employee_id}?fields=firstName,lastName,department,jobTitle,hireDate,supervisorEId,performanceScore",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "department", customName: "department", type: "string" },
            { name: "hireDate", customName: "hire_date", type: "string" },
            { name: "jobTitle", customName: "job_title", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: exit analysis accuracy", 520, "75"),
      slackNode(
        "6",
        "Slack: Post exit insights to HR channel",
        640,
        "📋 *Exit Interview Analysis*\\n\\nEmployee: {variables.employee_name} ({variables.job_title})\\nDepartment: {variables.department} | Tenure: {variables.tenure_months}mo\\n\\n*Primary Reason:* {variables.departure_reason}\\n*Sentiment:* {variables.exit_sentiment}\\n*Team Retention Risk:* {variables.team_retention_risk}\\n\\n*Actionable Insights:*\\n{variables.actionable_insights}\\n\\nConfidence: {variables.ai_confidence}% | Case: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "HRBP: Review exit analysis",
        640,
        "AI analyzed exit interview for {variables.employee_name} ({variables.department}) with {variables.ai_confidence}% confidence. Departure reason: {variables.departure_reason}. Team retention risk: {variables.team_retention_risk}. Review before sharing insights.",
        12,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Insights posted to Slack",
        760,
        "POST",
        "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        [{ key: "Content-Type", value: "application/json" }],
        [{ name: "ok", customName: "insights_posted", type: "boolean" }],
      ),
      conditionNode(
        "9",
        "Check: Insights delivered",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Exit interview audit trail",
        1000,
        "ExitInterviews",
        '[["{variables.case_id}","{variables.employee_name}","{variables.department}","{variables.departure_reason}","{variables.team_retention_risk}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 38. Elite: Policy Acknowledgment Tracker ───
  {
    name: "Elite: Policy Acknowledgment Tracker",
    description:
      "Triggers when a new company policy is published. AI resolves the target audience from BambooHR employee data and generates personalized acknowledgment requests. High-confidence audience targeting auto-sends; ambiguous scoping goes to compliance for review.",
    category: "HR & People Ops",
    tags: [
      "policy",
      "acknowledgment",
      "compliance",
      "bamboohr",
      "email-dispatch",
    ],
    nodes: [
      trigger("Policy published: Acknowledgment required", "webhook_app", {
        source: "confluence",
        event: "page.published",
        howItStarts:
          "Fires when a new or updated policy document is published requiring employee acknowledgment",
        setup:
          "Connect policy management system webhook for publish events. Payload includes policy_id, policy_title, policy_type, effective_date, and required_audience_tags.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "policy_acknowledgment_tracker",
      ),
      aiNodeV2(
        "3",
        "AI: Resolve audience + generate ack request",
        280,
        "You are an HR compliance specialist. Given a policy document and audience tags, resolve the exact employee audience from the employee directory. Determine: target_audience_description, estimated_employee_count, departments_affected, acknowledgment_deadline (based on policy type: regulatory=7 days, operational=14 days, informational=30 days), email_subject, and personalized_email_body that explains the policy change clearly and includes a one-click acknowledgment link.",
        "Policy: {variables.policy_title} — Type: {variables.policy_type} — Effective: {variables.effective_date} — Audience tags: {variables.audience_tags} — Employee data: {variables.employee_list_json} — Previous version changes: {variables.change_summary}",
        [
          {
            field: "estimated_employee_count",
            as: "target_count",
            type: "number",
          },
          {
            field: "departments_affected",
            as: "departments_affected",
            type: "string",
          },
          {
            field: "acknowledgment_deadline",
            as: "ack_deadline",
            type: "string",
          },
          { field: "email_body", as: "ack_email_body", type: "string" },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch employee list by audience", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employees",
              customName: "employee_list_json",
              type: "string",
            },
            {
              name: "employees.length",
              customName: "total_employees",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: audience targeting accuracy",
        520,
        "75",
      ),
      emailNode(
        "6",
        "Email: Send acknowledgment requests",
        640,
        "{variables.target_email_list}",
        "Action Required: Acknowledge '{variables.policy_title}' by {variables.ack_deadline}",
        "{variables.ack_email_body}\\n\\nPolicy: {variables.policy_title}\\nEffective Date: {variables.effective_date}\\nDeadline: {variables.ack_deadline}\\n\\nPlease click the link below to acknowledge receipt and understanding:\\n[Acknowledge Policy]({variables.ack_link})\\n\\nTracking ID: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "Compliance: Review audience scope",
        640,
        "AI identified {variables.target_count} employees across {variables.departments_affected} for policy '{variables.policy_title}'. Deadline: {variables.ack_deadline}. Confidence: {variables.ai_confidence}%. Review audience before sending acknowledgment requests.",
        12,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Acknowledgment requests sent",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=policy-ack-{variables.policy_id}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          {
            name: "TotalCount",
            customName: "ack_requests_sent",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "send_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Requests dispatched successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Policy acknowledgment audit trail",
        1000,
        "PolicyAck",
        '[["{variables.case_id}","{variables.policy_title}","{variables.target_count}","{variables.departments_affected}","{variables.ack_deadline}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 39. Elite: Equipment Provisioning ───
  {
    name: "Elite: Equipment Provisioning",
    description:
      "Triggers 7 days before a new hire's start date to determine equipment needs based on role and location from BambooHR. AI generates provisioning requirements and creates Jira tickets for IT. Standard setups auto-create; unusual configurations require IT manager approval.",
    category: "HR & People Ops",
    tags: ["equipment", "provisioning", "jira", "bamboohr", "it-operations"],
    nodes: [
      trigger("New hire: T-7 days to start date", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to check for new hires starting in 7 days who need equipment provisioned",
        setup:
          "Configure BambooHR API to query employees by hire date. Set up Jira project for IT provisioning tickets. Define standard equipment bundles by role and location.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "equipment_provisioning",
      ),
      aiNodeV2(
        "3",
        "AI: Determine equipment needs by role",
        280,
        "You are an IT provisioning specialist. Based on the employee's role, department, and location, determine the required equipment package. Standard bundles: Engineering (MacBook Pro 16, 2 monitors, mechanical keyboard, headset), Design (MacBook Pro 16, 4K monitor, Wacom tablet, headset), Sales (MacBook Air, headset, webcam), Operations (MacBook Air, monitor, headset). Remote employees get a home office stipend ($500). Output: equipment_list (array of items), total_estimated_cost, provisioning_priority (standard, rush, custom), special_requirements, and shipping_address_type (office, remote).",
        "New hire: {variables.employee_name} — Role: {variables.job_title} — Department: {variables.department} — Location: {variables.location} — Work type: {variables.work_type} — Start date: {variables.start_date} — Manager request notes: {variables.manager_notes}",
        [
          {
            field: "equipment_list",
            as: "equipment_list_json",
            type: "string",
          },
          {
            field: "total_estimated_cost",
            as: "estimated_cost",
            type: "number",
          },
          { field: "provisioning_priority", as: "priority", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch role + location details", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{variables.employee_id}?fields=firstName,lastName,department,jobTitle,location,hireDate,workEmail",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "department", customName: "department", type: "string" },
            { name: "location", customName: "location", type: "string" },
            { name: "jobTitle", customName: "job_title", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Confidence gate: provisioning accuracy", 520, "75"),
      n("6", "hitApi", "Jira: Create provisioning tickets", 640, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_it_project_key}"},"summary":"Equipment Provisioning: {variables.employee_name} — {variables.job_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Equipment needed:\\n{variables.equipment_list_json}\\n\\nEstimated cost: ${variables.estimated_cost}\\nPriority: {variables.priority}\\nStart date: {variables.start_date}\\nSpecial requirements: {variables.special_requirements}"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"High"}}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_issue_id", type: "string" },
            { name: "key", customName: "jira_issue_key", type: "string" },
          ],
        },
      }),
      approvalNode(
        "7",
        "IT manager: Approve non-standard setup",
        640,
        "AI determined equipment for {variables.employee_name} ({variables.job_title}, {variables.department}): ${variables.estimated_cost} estimated cost. Priority: {variables.priority}. Special requirements: {variables.special_requirements}. Confidence: {variables.ai_confidence}%. Approve to create Jira tickets.",
        12,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Jira tickets created",
        760,
        "GET",
        "https://{jira_domain}.atlassian.net/rest/api/3/issue/{variables.jira_issue_key}",
        [{ key: "Authorization", value: "Basic {jira_api_key}" }],
        [
          {
            name: "fields.status.name",
            customName: "ticket_status",
            type: "string",
          },
          { name: "key", customName: "verified_issue_key", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Tickets created successfully",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Equipment provisioning audit trail",
        1000,
        "EquipmentProvisioning",
        '[["{variables.case_id}","{variables.employee_name}","{variables.job_title}","{variables.estimated_cost}","{variables.priority}","{variables.jira_issue_key}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ─── 40. Elite: Certification Renewal Alerts ───
  {
    name: "Elite: Certification Renewal Alerts",
    description:
      "Runs on a weekly schedule to scan BambooHR for certifications expiring in the next 60 days. AI prioritizes by regulatory importance and identifies employees who need renewal reminders. Auto-sends for clear cases; routes ambiguous certification data to HR for validation.",
    category: "HR & People Ops",
    tags: ["certifications", "renewal", "bamboohr", "compliance", "scheduling"],
    nodes: [
      trigger("Schedule: Weekly certification expiry scan", "schedule", {
        schedule: "0 8 * * 2",
        howItStarts:
          "Runs every Tuesday at 8 AM to identify certifications expiring in the next 60 days",
        setup:
          "Configure BambooHR API to access custom certification fields. Define certification types and their regulatory requirements. Ensure email service is configured for reminder dispatch.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case ID + metadata",
        160,
        "certification_renewal_alerts",
      ),
      aiNodeV2(
        "3",
        "AI: Identify expiring certifications",
        280,
        "You are an HR compliance specialist focused on professional certifications. Analyze employee certification data to identify: (1) expiring_within_30_days (urgent), (2) expiring_within_60_days (upcoming), (3) already_expired (critical). For each, provide employee_name, certification_name, expiry_date, days_until_expiry, regulatory_impact (high if required for role, medium if preferred, low if optional), renewal_process (instructions or link), and priority (critical, high, medium, low). Summarize totals and highest-priority items.",
        "Certification data: {variables.cert_data_json} — Current date: {variables.current_date} — Role-required certifications: PMP for project managers, AWS/Azure for cloud engineers, CPA for finance, PHR/SPHR for HR, CISSP for security",
        [
          {
            field: "expiring_soon_count",
            as: "expiring_count",
            type: "number",
          },
          {
            field: "already_expired_count",
            as: "expired_count",
            type: "number",
          },
          {
            field: "renewal_reminders",
            as: "renewal_reminders_json",
            type: "string",
          },
          {
            field: "compliance_summary",
            as: "cert_compliance_summary",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "BambooHR: Fetch certification data", 400, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/all?fields=firstName,lastName,department,jobTitle,customCertificationName,customCertificationExpiry",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "employees", customName: "cert_data_json", type: "string" },
            {
              name: "employees.length",
              customName: "total_employees_checked",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate(
        "5",
        "Confidence gate: certification analysis accuracy",
        520,
        "75",
      ),
      emailNode(
        "6",
        "Email: Send renewal reminders",
        640,
        "{variables.reminder_recipients}",
        "Certification Renewal Required: Action needed within {variables.days_to_deadline} days",
        "Hi,\\n\\nThis is a reminder that one or more of your professional certifications requires renewal:\\n\\n{variables.renewal_reminders_json}\\n\\nExpiring soon: {variables.expiring_count} certifications\\nAlready expired: {variables.expired_count} certifications\\n\\nPlease initiate your renewal process as soon as possible to maintain compliance and avoid role restrictions.\\n\\nIf you have already renewed, please update your certification details in BambooHR.\\n\\nTracking ID: {variables.case_id}",
      ),
      approvalNode(
        "7",
        "HR: Validate ambiguous certification data",
        640,
        "AI identified {variables.expiring_count} expiring and {variables.expired_count} expired certifications with {variables.ai_confidence}% confidence. Summary: {variables.cert_compliance_summary}. Please validate before sending reminders — some certification records may be outdated.",
        24,
        420,
      ),
      verifyNode(
        "8",
        "Verify: Reminders sent successfully",
        760,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=cert-renewal&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "reminders_sent", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "reminder_delivery_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Reminders delivered",
        880,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Log: Certification renewal audit trail",
        1000,
        "CertRenewals",
        '[["{variables.case_id}","{variables.expiring_count}","{variables.expired_count}","{variables.cert_compliance_summary}","{variables.ai_confidence}","{variables.started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },
];
